import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type * as Y from "yjs"
import type { Editor } from "@tiptap/react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import { VocabularyEntryOutcomeEnum, VocabularyEntryStateEnum } from "@/__generated__/graphql"
import {
  askKeepSpelling,
  canKeepSpelling,
  entryDisplayText,
  invalidateAfterCapture,
  retryErrorKey,
  roleCanKeepSpelling,
  useLessonVocabularyEntries,
  useVocabularyEntryUpdates,
  type VocabularyEntryUpdate,
} from "@/lib/vocabulary-entries"
import { removeFlashcardCapture, retryFlashcardCapture, revertFlashcardCorrection } from "@/lib/live/flashcard-capture"
import { claimCorrectionAnnouncement } from "@/lib/correction-announcements"
import {
  CAPTURES_ARRAY,
  appendCaptureEvent,
  captureEventFrom,
  reduceCaptureEvents,
  type StoredCaptureEvent,
  type CaptureRow,
} from "@/lib/live/captures-log"
import { syncCaptureMarks } from "./capture-mark"

export interface LessonCapturesApi {
  // The strip's rows: latest state per capture, cancelled ones gone.
  rows: CaptureRow[]
  // Append an entry this client just created (the popover's onCaptured).
  recordEntry: (entry: VocabularyEntryUpdate) => void
  // Cancel a capture on the server, then reflect it locally at once.
  undo: (entryId: string) => Promise<void>
  // Re-enqueue a failed capture, then list the re-queued entry at once.
  retry: (entryId: string) => Promise<void>
  // Put the student's own spelling back on a corrected capture.
  keepSpelling: (entryId: string) => Promise<void>
  // Entries whose revert this client asked for and which are still corrected.
  restoringIds: string[]
}

// Owns the classroom's view of the lesson's captures (#825 phase 2). Rows are
// a reduction of the shared `captures` Y.Array; three writers feed it — the
// capturer on capture, whichever client receives a subscription event, and
// hydration from lessonVocabularyEntries on mount (a no-op when the log is
// ahead). When the collab transport is off nothing replicates to the peer, but
// every server-truth event still reaches both parties through the subscription,
// so each client keeps its own accurate log. The TipTap marks follow the rows.
// Undo eligibility (owner any time, tutor within 60 s) is the strip's business;
// the server enforces the real rule and this hook only reports the refusal.
export function useLessonCaptures({
  doc,
  lessonId,
  editor,
  viewerRole,
}: {
  doc: Y.Doc
  lessonId: string | undefined
  editor: Editor | null
  viewerRole: string | undefined
}): LessonCapturesApi {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const log = useMemo(() => doc.getArray<StoredCaptureEvent>(CAPTURES_ARRAY), [doc])
  const [rows, setRows] = useState<CaptureRow[]>(() => reduceCaptureEvents(log.toArray()))
  // The async actions read the rows through a ref so they never close over a
  // stale render.
  const rowsRef = useRef(rows)
  useEffect(() => {
    rowsRef.current = rows
  }, [rows])

  // Re-reduce on every log change, local or remote.
  useEffect(() => {
    const refresh = () => setRows(reduceCaptureEvents(log.toArray()))
    refresh()
    log.observe(refresh)
    return () => log.unobserve(refresh)
  }, [log])

  // Mount-time hydration (both participants are authorized). Idempotent — the
  // no-op rule in appendCaptureEvent skips anything the log already holds.
  const hydration = useLessonVocabularyEntries(lessonId)
  useEffect(() => {
    for (const entry of hydration.data ?? []) appendCaptureEvent(doc, captureEventFrom(entry))
  }, [doc, hydration.data])

  // The markers on the highlighted text follow the rows: removed on cancel,
  // re-styled on a state change.
  useEffect(() => {
    if (editor) syncCaptureMarks(editor, rows)
  }, [editor, rows])

  const undo = useCallback(
    async (entryId: string) => {
      const row = rowsRef.current.find((r) => r.entryId === entryId)
      try {
        const result = await removeFlashcardCapture(entryId)
        if (!result.success) {
          // `forbidden` is the server's can_remove? saying no: for a tutor, the
          // 60 s buffer closed between the click and the server's clock. That
          // is not a transient failure, and "try again" would be a lie.
          toast.error(
            t(result.errorCode === "forbidden" ? "liveSession.vocabularyStrip.undoTooLateToast" : "liveSession.vocabularyStrip.undoFailedToast"),
          )
          return
        }
        // Reflect the cancel now rather than waiting for the broadcast (which
        // also arrives, and is a no-op if this local event is already newer).
        // Client clock, floored at the row's own server-stamped updatedAt: a
        // lagging client clock must never stamp this older than the event it
        // is cancelling, or appendCaptureEvent's guard drops it and the row
        // sticks around until the broadcast arrives (or forever, offline). An
        // equal timestamp is fine — the state differs, so the reduction still
        // elects the cancel.
        if (row) {
          appendCaptureEvent(doc, {
            ...row,
            state: VocabularyEntryStateEnum.Cancelled,
            outcome: VocabularyEntryOutcomeEnum.Cancelled,
            updatedAt: new Date(Math.max(Date.now(), Date.parse(row.updatedAt))).toISOString(),
          })
        }
        invalidateAfterCapture(qc)
        toast.success(t("liveSession.vocabularyStrip.undoneToast"))
      } catch {
        toast.error(t("liveSession.vocabularyStrip.undoFailedToast"))
      }
    },
    [doc, qc, t],
  )

  const retry = useCallback(
    async (entryId: string) => {
      const row = rowsRef.current.find((r) => r.entryId === entryId)
      try {
        const result = await retryFlashcardCapture(entryId)
        if (result.errorCode || !result.vocabularyEntry) {
          toast.error(t(retryErrorKey(result.errorCode)))
          return
        }
        appendCaptureEvent(doc, captureEventFrom(result.vocabularyEntry))
        invalidateAfterCapture(qc)
        // The word the row is HEADED by, like its Retry button: on a corrected
        // row the button and the toast that follows it must not answer with
        // two different spellings.
        const requeued = result.vocabularyEntry
        toast.success(
          t("liveSession.vocabularyStrip.retryQueuedToast", {
            word: row
              ? entryDisplayText(row.text, row.correctedText)
              : entryDisplayText(requeued.sourceText, requeued.correctedText),
          }),
        )
      } catch {
        toast.error(t("liveSession.vocabularyStrip.retryFailedToast"))
      }
    },
    [doc, qc, t],
  )

  // Reverts this client asked for. Kept until the entry stops being corrected
  // (the job's completed broadcast clears correctedText), so the row says
  // "Restoring…" for the whole rebuild rather than flickering back to an
  // action the server would now refuse with in_progress. A revert whose job
  // fails leaves the row restoring — the strip has no failure signal
  // (contract R5, accepted); Browse's regenerationStatus shows it.
  //
  // Derived from `rows`, never from rowsRef: the ref is assigned in an effect
  // AFTER render, so a memo reading it would see the previous rows and never
  // notice the rebuilt entry clearing correctedText.
  const [revertRequested, setRevertRequested] = useState<string[]>([])
  const restoringIds = useMemo(
    () => revertRequested.filter((id) => rows.find((r) => r.entryId === id)?.correctedText),
    [revertRequested, rows],
  )

  const keepSpelling = useCallback(
    async (entryId: string) => {
      setRevertRequested((ids) => (ids.includes(entryId) ? ids : [...ids, entryId]))
      // The asking and the reading are shared with the chat and Browse
      // (askKeepSpelling): every refusal through revertErrorKey, and no toast
      // when it is accepted — the strip's action stays mounted and renames
      // itself to "Restoring your spelling…", so a toast would say it a second
      // time. The strip owns the line for a request that never arrived, and
      // what it does with the entry it gets back.
      const entry = await askKeepSpelling(() => revertFlashcardCorrection(entryId), {
        t,
        transportErrorKey: "liveSession.vocabularyStrip.revertFailedToast",
      })
      if (!entry) {
        setRevertRequested((ids) => ids.filter((id) => id !== entryId))
        return
      }
      // The server has stamped skip_correction; the card itself is rebuilt
      // by the job, whose broadcast ends the restoring state.
      appendCaptureEvent(doc, captureEventFrom(entry))
      invalidateAfterCapture(qc)
    },
    [doc, qc, t],
  )

  const recordEntry = useCallback(
    (entry: VocabularyEntryUpdate) => {
      appendCaptureEvent(doc, captureEventFrom(entry))
      // ONLY a corrected completion interrupts: the card was created under a
      // spelling nobody typed. A plain "created" is the strip's business.
      if (entry.state !== VocabularyEntryStateEnum.Completed) return
      // The shared rule for "is there a correction still worth reverting" —
      // skipCorrection is the server's "a revert is under way or done", and a
      // blank correction is no correction. The same call the chat makes, so
      // the two surfaces cannot answer one capture differently.
      if (!canKeepSpelling(entry)) return
      // The claim is SHARED with the chat's conversation view (#825 phase 3):
      // one capture can carry both a lesson and a source message, and in this
      // room the notes pane and the chat panel each hear it. Whoever claims the
      // id announces; hydration appends through appendCaptureEvent and never
      // comes here, so re-entering the room re-announces nothing either.
      if (!claimCorrectionAnnouncement(entry.id)) return
      // The same rule the strip's own action renders under. This toast only
      // ever fires on a COMPLETED capture, so in practice it offers the action
      // to the student alone: the server grants a tutor the free path only, and
      // past generation would answer their tap not_found. A tutor — like an
      // admin, or a viewer whose role has not loaded — is told what happened
      // and offered nothing.
      const participant = roleCanKeepSpelling(viewerRole, entry.state)
      // One sentence for both roles: the strip's toast says what the card was
      // created as and what it was corrected from, and neither half is about
      // whose card it is (unlike the chat's, whose tutor line names the
      // student). A second key held seventeen byte-identical leaves and a dead
      // branch over them.
      toast.success(
        t("liveSession.vocabularyStrip.correctedToast", {
          corrected: entryDisplayText(entry.sourceText, entry.correctedText),
          original: entry.sourceText,
        }),
        participant
          ? {
              // First-person, with no role branch: the guard above runs only on
              // a COMPLETED capture, where roleCanKeepSpelling grants the action
              // to the student alone — a tutor's tap would be refused not_found.
              // Its twin in the conversation view lost the same dead arm.
              action: {
                label: t("liveSession.vocabularyStrip.keepSpelling"),
                onClick: () => {
                  void keepSpelling(entry.id)
                },
              },
            }
          : undefined,
      )
    },
    [doc, keepSpelling, t, viewerRole],
  )

  // Server truth: whichever client receives an event appends it; Yjs carries
  // it to the peer when the transport is up. Must stay BELOW recordEntry.
  useVocabularyEntryUpdates(lessonId ? { lessonId } : null, recordEntry)

  return { rows, recordEntry, undo, retry, keepSpelling, restoringIds }
}
